import * as React from "react";
import {useMemo} from "react";
import {Chip, Grid2, Grid2 as Grid, Pagination, useTheme} from "@mui/material";
import {QuoteDTO, QuoteStatus, RequestStatus} from "@/api/data-contracts";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {CardEx} from "@/lib/components/CardEx";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import {useTranslation} from "react-i18next";
import {CommonI18N, RequestI18N} from "@/translations";
import {ChangeHandler} from "@/lib/modelstate";
import {Constants} from "@/api/constants";
import AutocompleteInputField from "@/lib/components/inputfields/auto-complete-input-field";
import {useEmployees} from "@/datacaches";
import {convertToThreeDigitString, ErrorsShape} from "@/lib/utils";
import {OrderDataType} from "@/model/orderData";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {QuoteStatusBadgeField} from "@/common/QuoteStatusBadge";
import {Badge} from "@/lib/components/Badge";
import {HakenIcon, XIcon} from "@/agentur/AgenturIcons";
import {color_red} from "@/agentur/AgenturColors";
import {getQuoteStatusColor} from "@/common/request-details/quote-status-field";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {DeclineReasonDisplay} from "@/common/request-cards/DeclineReasonDisplay";

type AcceptRowProps = {
    isOperator: boolean;
    status?: QuoteStatus;
    requestStatus?: RequestStatus;
    paletteMode: "light" | "dark";
    handleConfirmQuote?: () => void;
    handleDismissQuote?: () => void;
    handleGiveFeedback?: () => void;
    canGiveFeedback?: boolean;
    quantityOptionSelected?: unknown;
    t: (key: string) => string;
};

/** Renders the accept/dismiss/feedback action row for a submitted quote, or a status badge otherwise. */
const AcceptRow = ({
    isOperator,
    status,
    requestStatus,
    paletteMode,
    handleConfirmQuote,
    handleDismissQuote,
    handleGiveFeedback,
    canGiveFeedback,
    quantityOptionSelected,
    t
}: AcceptRowProps) => {
    if (!isOperator && status === QuoteStatus.SUBMITTED) {
        return (
            <Grid container size={{ sm: 12 }} spacing={1}>
                {requestStatus === RequestStatus.TO_BE_CONFIRMED && (
                    <>
                        <Grid size={{ sm: 6 }}>
                            <ButtonEx
                                startIcon={<HakenIcon mode={paletteMode} />}
                                sx={{ backgroundColor: getQuoteStatusColor(paletteMode, QuoteStatus.ACCEPTED) }}
                                label={t(RequestI18N.details.dialog.confirmQuoteTitle)}
                                onClick={handleConfirmQuote}
                                fullWidth
                                disabled={quantityOptionSelected == null}
                            />
                        </Grid>
                        <Grid size={{ sm: 6 }}>
                            <ButtonEx
                                startIcon={<XIcon mode={paletteMode} />}
                                sx={{ backgroundColor: getQuoteStatusColor(paletteMode, QuoteStatus.DISMISSED) }}
                                label={t(RequestI18N.details.dialog.dismissQuoteTitle)}
                                onClick={handleDismissQuote}
                                fullWidth
                            />
                        </Grid>
                    </>
                )}
                {canGiveFeedback && handleGiveFeedback && (
                    <Grid size={{ sm: 12 }}>
                        <ButtonEx
                                label={t(RequestI18N.details.action.giveFeedback)}
                            onClick={handleGiveFeedback}
                            fullWidth
                            color="warning"
                        />
                    </Grid>
                )}
            </Grid>
        );
    }
    return (
        <Grid size={{ sm: 12 }}>
            <QuoteStatusBadgeField status={status} />
        </Grid>
    );
};

type VersionPaginationProps = {
    companyVersions: QuoteDTO[];
    versionIdx: number;
    setVersionIdx: (idx: number) => void;
};

/** Renders a pagination control for switching between quote versions of a single company. */
const VersionPagination = ({ companyVersions, versionIdx, setVersionIdx }: VersionPaginationProps) => (
    <Grid size={{ sm: 12 }} container justifyContent="flex-start">
        <Pagination
            count={companyVersions.length}
            page={versionIdx + 1}
            variant="outlined"
            shape="rounded"
            onChange={(_, page) => setVersionIdx(page - 1)}
        />
    </Grid>
);

export type QuoteCardProps = {
    model?: QuoteDTO;
    errors?: ErrorsShape<QuoteDTO>;
    requestStatus?: RequestStatus;
    onChange: ChangeHandler;
    readonly?: boolean;
    mode?: OrderDataType;
    quotes?: QuoteDTO[];
    quoteIdx?: number;
    setQuoteIdx?: (idx: number) => void;
    /** Versions of the current company's quote, used for inner version pagination below the divider. */
    companyVersions?: QuoteDTO[];
    /** Currently selected version index within companyVersions. */
    versionIdx?: number;
    /** Callback to change the selected version index. */
    setVersionIdx?: (idx: number) => void;
    handleConfirmQuote?: () => void;
    handleDismissQuote?: () => void;
    handleGiveFeedback?: () => void;
    canGiveFeedback?: boolean;
    onStartRevision?: () => void;
    canStartRevision?: boolean;
    showRejectReasonUnder?: "status" | "contact";
};

export const QuoteCard = ({
    model,
    errors,
    requestStatus,
    readonly,
    onChange,
    mode,
    quotes,
    quoteIdx,
    setQuoteIdx,
    companyVersions,
    versionIdx,
    setVersionIdx,
    handleConfirmQuote,
    handleDismissQuote,
    handleGiveFeedback,
    canGiveFeedback,
    onStartRevision,
    canStartRevision,
    showRejectReasonUnder
}: QuoteCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { roles } = useAuth();
    const isOperator = isOperatorRole(roles);
    const [employees] = useEmployees();
    const versionLabel = t(RequestI18N.details.field.quoteVersionSuffix, {version: model?.quoteVersion ?? 1});
    const titleText =
            mode === "accept"
                    ? t(RequestI18N.details.page.quoteFromCompanyTitle, {companyName: model?.company?.name ?? ""})
                    : t(RequestI18N.details.page.quoteTitle);

    const availableUsers = useMemo(() => employees, [employees]);

    const title = (
        <Grid2 container direction="row">
            <Grid2 flexGrow={1}>
                <span>{titleText}</span>
                &nbsp;
                {model?.withdrawnAfterSubmission ? (
                    <Badge
                            label={t(RequestI18N.details.message.quoteWithdrawnAfterSubmission)}
                        backgroundColor={color_red[theme.palette.mode === "dark" ? 2 : 3]}
                    />
                ) : null}
            </Grid2>
            {setQuoteIdx && quotes && quotes?.length > 1 ? (
                <Grid2>
                    <Pagination
                        count={quotes.length}
                        page={(quoteIdx ?? 0) + 1}
                        variant="outlined"
                        shape="rounded"
                        onChange={(e, page) => setQuoteIdx(page - 1)}
                    />
                </Grid2>
            ) : null}
        </Grid2>
    );

    const acceptRow =
        mode === "accept" ? (
            <AcceptRow
                isOperator={isOperator}
                status={model?.status}
                requestStatus={requestStatus}
                paletteMode={theme.palette.mode}
                handleConfirmQuote={handleConfirmQuote}
                handleDismissQuote={handleDismissQuote}
                handleGiveFeedback={handleGiveFeedback}
                canGiveFeedback={canGiveFeedback}
                quantityOptionSelected={model?.orderData?.quantityOptionSelected}
                t={t}
            />
        ) : null;

    return (
        <CardEx title={title} sx={{ backgroundColor: getQuoteStatusColor(theme.palette.mode, model?.status) }}>
            <Grid container direction="row" spacing={2} size={{ sm: 12 }}>
                {companyVersions && companyVersions.length > 1 && (
                    <Grid size={{ sm: 12 }} container alignItems="center" spacing={1}>
                        {setVersionIdx && (
                            <Grid>
                                <Pagination
                                    count={companyVersions.length}
                                    page={(versionIdx ?? 0) + 1}
                                    variant="outlined"
                                    shape="rounded"
                                    onChange={(_, page) => setVersionIdx(page - 1)}
                                />
                            </Grid>
                        )}
                        <Grid flexGrow={1} display="flex" justifyContent="flex-end">
                            <Chip label={versionLabel} size="small" color="primary" />
                        </Grid>
                    </Grid>
                )}
                <Grid size={{ sm: 12 }} container direction="row" spacing={2}>
                    <Grid size={{ sm: 6 }}>
                        <StringInputField
                            name="customerReferenceNumber"
                            value={model?.customerReferenceNumber}
                            label={t(RequestI18N.details.field.customerQuoteNumber)}
                            error={errors?.customerReferenceNumber}
                            maxChars={Constants.CUSTOMER_REFERENCE_NUMBER_LENGTH_MAX}
                            activatePrefix
                            onChange={onChange}
                            prefixValue={
                                model?.company?.number == null
                                        ? t(CommonI18N.message.notGiven)
                                    : `${convertToThreeDigitString(model?.company?.number)} -`
                            }
                            disabled={readonly}
                        />
                    </Grid>
                    <Grid size={{ sm: 6 }}>
                        <DateInputField
                            name="validUntil"
                            label={t(RequestI18N.details.field.quoteValidity)}
                            value={model?.validUntil}
                            error={errors?.validUntil}
                            onChange={onChange}
                            disabled={readonly}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 6 }}>
                    <DateInputField
                        name="orderData.deliveryDateFrom"
                        label={t(RequestI18N.details.field.deliveryDateFrom)}
                        value={model?.orderData?.deliveryDateFrom}
                        error={errors?.orderData?.deliveryDateFrom}
                        onChange={onChange}
                        disabled={readonly}
                    />
                </Grid>
                <Grid size={{ sm: 6 }}>
                    <DateInputField
                        name="orderData.deliveryDateTo"
                        label={t(RequestI18N.details.field.deliveryDateTo)}
                        value={model?.orderData?.deliveryDateTo}
                        error={errors?.orderData?.deliveryDateTo}
                        onChange={onChange}
                        disabled={readonly}
                    />
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <StringInputField
                        name="additionalInformation"
                        label={t(RequestI18N.details.field.quoteInformation)}
                        value={model?.additionalInformation}
                        error={errors?.additionalInformation}
                        maxChars={Constants.ADDITIONAL_INFORMATION_LENGTH_MAX}
                        onChange={onChange}
                        multiline
                        disabled={readonly}
                    />
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <StringInputField
                        name="orderData.paymentTerms"
                        label={t(RequestI18N.details.field.paymentTerms)}
                        value={model?.orderData?.paymentTerms}
                        onChange={onChange}
                        error={errors?.orderData?.paymentTerms}
                        multiline
                        maxChars={Constants.PAYMENT_TERMS_LENGTH_MAX}
                        disabled={readonly}
                    />
                </Grid>
                <Grid size={{ sm: 12 }}>
                    <AutocompleteInputField
                        name="contactPerson"
                        label={t(RequestI18N.details.field.contactPerson)}
                        onChange={onChange}
                        value={model?.contactPerson}
                        error={errors?.contactPerson}
                        options={availableUsers}
                        getOptionLabel={(option) =>
                            (option && `${option.firstName} ${option.lastName} <${option.email}>`) ?? ""
                        }
                        disabled={readonly}
                    />
                </Grid>
                {model?.feedback && (
                    <Grid size={{ sm: 12 }}>
                        <StringInputField
                                label={t(RequestI18N.details.field.receivedFeedback)}
                            value={model.feedback}
                            multiline
                            minRows={2}
                            disabled
                        />
                    </Grid>
                )}
                {mode === "quote" && canStartRevision && onStartRevision && (
                    <Grid size={{ sm: 12 }}>
                        <ButtonEx
                                label={t(RequestI18N.details.action.startRevision)}
                            onClick={onStartRevision}
                            color="success"
                            fullWidth
                        />
                    </Grid>
                )}
                {showRejectReasonUnder === "contact" && model?.status === QuoteStatus.DISMISSED ? (
                    <DeclineReasonDisplay
                        declineReason={model?.declineReason}
                        explanation={model?.rejectionReason}
                    />
                ) : null}
                {mode === "accept" && acceptRow}
                {showRejectReasonUnder === "status" && model?.status === QuoteStatus.DISMISSED ? (
                    <DeclineReasonDisplay
                        declineReason={model?.declineReason}
                        explanation={model?.rejectionReason}
                    />
                ) : null}
            </Grid>
        </CardEx>
    );
};
